import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { motion } from 'framer-motion';
import toastUtils from '../utils/toast-utils';
import { X, RotateCcw, Copy, QrCode, Printer } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * The CardPreviewModal is the primary interaction point for viewing and printing an ID Card.
 * It manages data loading for the specific card, fetches dynamic assets/settings,
 * and holds the complex standalone HTML template needed for accurate physical printing.
 */
export default function CardPreviewModal({ cardId, onClose }) {
  const [isFlipped, setIsFlipped] = useState(false);

  // Fetch card data
  const { data: card, isLoading: cardLoading } = useQuery({
    queryKey: ['card', cardId],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/cards/${cardId}`);
      return data;
    },
  });

  // Fetch settings and assets
  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/settings`);
      return data;
    },
  });

  /**
   * Copies the public verification URL to the clipboard.
   * Utilizes the modern navigator clipboard API with a legacy fallback for broad compatibility.
   */
  const copyVerifyUrl = async () => {
    if (card?.qr_verify_url) {
      try {
        await navigator.clipboard.writeText(card.qr_verify_url);
        toastUtils.success('Verify URL copied to clipboard');
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = card.qr_verify_url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          toastUtils.success('Verify URL copied to clipboard');
        } catch (err) {
          toastUtils.error('Failed to copy URL');
        }
        document.body.removeChild(textArea);
      }
    }
  };

  /**
   * Core logic for the "Print / Save as PDF" feature.
   * Generates a completely independent HTML/CSS document in a hidden popup window
   * tailored for exact physical ID card dimensions.
   */
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toastUtils.error('Please allow popups to print the card');
      return;
    }

    const s = settingsData?.settings || {};
    const assets = settingsData?.assets || {};

    const logoUrl = assets.logo?.url;
    const badgeUrl = assets.badge?.url;
    const preambleUrl = assets.preamble?.url;
    const stampUrl = assets.stamp?.url;
    const signUrl = assets.signature?.url;

    const approvedBy = s.approved_by;
    const ministry = s.ministry;
    const rni = s.rni_number;
    const regdOff = s.regd_office;
    const headOff = s.head_office;
    const phone = s.phone_primary;
    const mob1 = s.mobile_primary;
    const mob2 = s.mobile_secondary;
    const email = s.email;
    const website = s.website;
    const notice = s.card_back_notice;
    const warning = s.card_back_warning;

    // The standard HTML document template for the print window
    const printContent = `<!DOCTYPE html>
<html>
<head>
  <title>Press Card - ${card?.full_name || 'Preview'}</title> <!-- Dynamic title for the PDF/Print window -->
  <style>
    /* Synchronize UI typography by importing professional fonts */
    @import url('https://fonts.googleapis.com/css2?family=IM+Fell+English:ital@0;1&family=Georgia&display=swap');
    
    /* Global reset: removes default browser margins to ensure precision */
    * { 
      margin: 0; 
      padding: 0; 
      box-sizing: border-box; /* Ensures padding doesn't increase element width */
    }
    
    /* Layout: Centers cards on screen for preview, uses flex for gap control */
    body { 
      background: #fff; 
      display: flex; 
      gap: 24px; /* Space between Front and Back of the card */
      padding: 20px; 
      font-family: Arial, sans-serif; 
    }

    /* ── FRONT SIDE ── */
    .card-front { 
      width: 280px; /* Standard ID card width */
      height: 410px; /* High-fidelity height matching physical scale */
      border: 1px solid #E5E5E5; /* Subtle border for visibility */
      display: flex; 
      overflow: hidden; /* Clips content to card bounds */
      position: relative; 
      page-break-inside: avoid; /* Prevents card from splitting across pages */
      margin: 0 auto; 
    }
    
    /* Vertical red branding bar */
    .left-bar { 
      width: 67px; 
      height: 100%; 
      background: #E31E24 !important; /* Brand Red - !important forces rendering in print */
      -webkit-print-color-adjust: exact; /* Crucial: enables background colors in Chrome/Safari */
      print-color-adjust: exact; 
      display: flex; 
      flex-direction: column; 
      align-items: center; 
      justify-content: flex-start; 
      padding-top: 10px; 
      padding-bottom: 2px; 
      gap: 5px; /* Vertical gap between letters */
      flex-shrink: 0; 
    }
    
    /* Vertically stacked "PRESS" lettering */
    .press-letter { 
      color: white !important; 
      font-family: "Arial Black", Gadget, sans-serif; 
      font-size: 80px; 
      font-weight: 900; 
      line-height: 0.9; 
      letter-spacing: 4px; 
      margin: 0; 
      display: block; 
    }
    
    /* Container for the right-side dynamic information */
    .right-col { 
      flex: 1; 
      display: flex; 
      flex-direction: column; 
      padding: 8px 4px 0px 0px; 
      overflow: hidden; 
      position: relative; 
    }
    
    /* Top logos area */
    .top-logos { 
      display: flex; 
      flex-direction: column; 
      align-items: center; 
    }
    .top-logos .preamble { height: 55px; object-fit: contain; margin-bottom: 2px; }
    .logo-row { display: flex; align-items: center; justify-content: center; gap: 0; }
    .logo-row .badge { height: 58px; width: 70px; object-fit: fill; }
    .logo-row .logo  { height: 58px; width: 98px; object-fit: fill; margin-left: -1px; } /* Tight overlap for brand accuracy */
    
    /* Labels for government approval and RNI numbering */
    .gov-info { text-align: center; margin-top: 6px; } /* Exact match to UI mt-[6px] */
    .gov-info .approved-lbl { font-size: 5.5px; font-weight: 500; color: #1A1A1A; text-transform: uppercase; letter-spacing: 0.025em; line-height: 1; margin-bottom: -1px; }
    .gov-info .gov-name { color: #E31E24; font-size: 13px; font-weight: bold; text-transform: uppercase; margin-top: 0; }
    
    /* Specialized vertical scaling (scaleY) for the Ministry line to match physical card aesthetics */
    .gov-info .ministry { 
      font-size: 7.9px; 
      font-weight: bold; 
      color: #1A1A1A; 
      line-height: 23px; 
      margin-top: -5px; 
      width: 100%; 
      transform: scaleY(2); /* Doubles vertical height of letters without affecting width */
      transform-origin: center; 
    }
    .gov-info .rni { font-size: 11.5px; font-weight: bold; color: #1A1A1A; margin-top: -1px; }
    
    /* Middle section housing the QR code and the profile photo with precise gap control */
    .middle { display: flex; justify-content: flex-start; align-items: flex-start; margin-top: 2px; padding: 0; gap: 2px; }
    .qr-box { width: 105px; height: 108px; display: flex; align-items: center; justify-content: center; background: #fff; margin-top: 0; }
    .qr-box img { width: 100%; height: 100%; object-fit: contain; mix-blend-mode: multiply; } /* Blending ensures QR code "sinks" into background */
    
    /* Profile photo container with negative margin for precise horizontal overlap */
    .photo-box { 
      width: 96px; 
      height: 110px; 
      position: relative; 
      background: #fff; /* bg-white */
      padding: 1px; /* p-[1px] */
      margin-left: -3px; /* Precise overlap matching the UI's ml-[-3px] */
      flex-shrink: 0; 
      margin-top: 0px; /* mt-[0px] */
      overflow: visible; /* Allows stamp/signature to overlap outside box */
    }
    .photo-inner { width: 100%; height: 100%; background: #f0f0f0; overflow: hidden; } /* Mirroring the inner div with bg-[#f0f0f0] */
    .photo-inner .main-photo { width: 100%; height: 100%; object-fit: cover; display: block; }
    
    /* Stamp and Signature: Positioned absolutely over the profile photo with realism blending */
    .photo-box .stamp { position: absolute; bottom: -12px; right: -3px; width: 60px; height: 60px; object-fit: contain; mix-blend-mode: multiply; z-index: 10; }
    .photo-box .sign  { position: absolute; bottom: -13px; right: 18px;  width: 45px; height: 55px; object-fit: contain; mix-blend-mode: multiply; z-index: 20; }
    
    /* Holder Information (Name / Title / UID) mirroring the UI typography */
.info { text-align: center; margin-top: 6px; margin-bottom: 0; width: 100%; }
    .info .name  { font-family: Georgia, "Times New Roman", serif; font-size: 15px; font-weight: 900; color: #000; line-height: 1.25; margin: 0; }
    .info .loc   { font-family: "Times New Roman"; font-size: 13px; font-weight: bold; color: #000; line-height: 1.25; margin-top: 1px; }
    .info .desig { font-family: "Times New Roman"; font-size: 13px; font-weight: bold; color: #000; line-height: 1.25; margin-top: 1px; }
    .info .idno  { font-family: "Times New Roman"; font-size: 13px; font-weight: bold; color: #000; line-height: 1.25; margin-top: 2px; }
    
    /* Fixed red bar at the bottom with Expiry Date and high-contrast typography */
.bottom-bar { display: flex; justify-content: center; width: calc(100% + 4px); margin-right: -4px; margin-top: auto; }
    .bottom-bar-inner { 
      flex: 1; 
      background: #E31E24 !important; 
      -webkit-print-color-adjust: exact; 
      color: white !important; 
      text-align: center; 
      padding: 6px 0; /* Matches UI py-[6px] */
      font-size: 18px; 
      font-weight: bold; 
      font-family: sans-serif; 
    }

    /* ── BACK SIDE STYLING ── */
    /* Defines physical bounds and layout for the card back (420px height) */
    .card-back { 
      width: 280px; 
      height: 420px; 
      border: 1px solid #E5E5E5; 
      display: flex; 
      flex-direction: column; 
      overflow: hidden; 
      page-break-inside: avoid; 
      margin: 0 auto; 
    }
    
    /* Large "PRESS" header with precise character tracking synchronization */
    .back-header { 
      background: #E31E24 !important; 
      -webkit-print-color-adjust: exact; 
      color: white !important; 
      text-align: center; 
      font-family: "Arial Black", Gadget, sans-serif; 
      font-size: 70px; 
      font-weight: 900; 
      letter-spacing: 5.6px; /* 0.08em sync for 70px font */
      line-height: 1; 
      height: 70px; 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      text-transform: uppercase; 
    }
    
    .back-body { flex: 1; display: flex; flex-direction: column; align-items: center; padding: 8px; text-align: center; overflow: hidden; } /* Sync: p-2 = 8px */
    .addr-title { color: #003399; font-weight: bold; font-size: 13px; margin-bottom: 4px; } /* Sync: mb-1 = 4px */
    .addr-text  { font-size: 11px; font-weight: bold; color: #1A1A1A; line-height: 1.25; max-width: 90%; margin-bottom: 4px; } /* Sync: mb-1 = 4px */
    .mob-text   { font-size: 12px; font-weight: bold; color: #1A1A1A; margin-bottom: 4px; } /* Sync: mb-1 = 4px */
    .notice-red { color: #E31E24; font-size: 11px; line-height: 1.25; margin-bottom: 4px; text-align: center; width: 100%; }
    .notice-blue { color: #003399; font-size: 11px; line-height: 1.25; margin-bottom: 6px; max-width: 100%; text-align: center; width: 100%; }
    
    .logos-row { display: flex; align-items: center; justify-content: center; margin-bottom: 4px; width: 100%; } /* Sync: mb-1 = 4px */
    .logos-row .b-badge { height: 65px; width: 80px; object-fit: fill; }
    .logos-row .b-logo  { height: 65px; width: 120px; object-fit: fill; margin-left: -1px; }
    
    /* Official address and contact list footer with distinct spacing for Regd vs Head office */
    .back-footer { font-size: 11px; font-family: Arial, sans-serif; line-height: 1.3; color: #555; width: 100%; padding-top: 2px; text-align: center; }
    .back-footer p.spaced-regd { margin-bottom: 2px; font-weight: bold; word-break: break-word; overflow-wrap: break-word; max-width: 100%; } 
    .back-footer p.spaced-head { margin-bottom: 4px; font-weight: bold; word-break: break-word; overflow-wrap: break-word; max-width: 100%; } 
    .back-footer p { margin-bottom: 1px; }
    .back-footer p.mt-spaced { margin-top: 4px; } /* mt-1 = 4px */
    .back-footer .lbl { color: #E31E24; font-weight: bold; } /* Labels use brand red */
    .back-footer .lbl-dark { color: #000; font-weight: bold; } /* Labels use pure black */
    .back-footer .val { color: #0D0D0D; } /* Values use slightly softened black */

    /* PRINT ENGINE OPTIMIZATION */
    @media print {
      body { padding: 0; margin: 0; }
      /* FORCES browsers to print backgrounds instead of white space */
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
      .card-front, .card-back { page-break-inside: avoid; } /* Prevents page-cuts in middle of card */
      .left-bar { background: #E31E24 !important; } /* Hardcoded fallback for older print engines */
      .bottom-bar-inner { background: #E31E24 !important; }
      .back-header { background: #E31E24 !important; }
    }
  </style>
</head>
<body>

  <!-- FRONT DESIGN: Renders the identity face of the card -->
  <div class="card-front">
    <div class="left-bar">
      <!-- "PRESS" stacked vertically letter by letter -->
      <span class="press-letter">P</span>
      <span class="press-letter">R</span>
      <span class="press-letter">E</span>
      <span class="press-letter">S</span>
      <span class="press-letter">S</span>
    </div>
    <div class="right-col">
      <div class="top-logos">
        <img src="${preambleUrl}" class="preamble" /> <!-- Official Preamble Banner -->
        <div class="logo-row">
          <img src="${badgeUrl}" class="badge" /> <!-- Organization Badge -->
          <img src="${logoUrl}"  class="logo" />  <!-- Main Organization Logo -->
        </div>
      </div>
      <div class="gov-info">
        <p class="approved-lbl">APPROVED BY</p>
        <p class="gov-name">${approvedBy}</p> <!-- Dynamic: Government / Organization Name -->
        <p class="ministry">${ministry}</p> <!-- Dynamic: Ministry / Agency Name -->
        <p class="rni">RNI: ${rni}</p> <!-- Registration Number -->
      </div>
      <div class="middle">
        <div class="qr-box">
          <!-- QR Code Injection: Uses base64 if available -->
          ${card?.qr_base64 ? `<img src="${card.qr_base64}" alt="QR"/>` : '<div style="font-size:9px;color:#ccc;text-align:center;">QR<br/>Pending</div>'}
        </div>
        <div class="photo-box">
          <!-- Inner container for background and clipping synchronization -->
          <div class="photo-inner">
            ${card?.photo_path ? `<img src="${API_URL}/src/photos/${card.photo_path}" class="main-photo" alt="Photo"/>` : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:32px;color:#666;font-weight:500;">${card?.full_name?.charAt(0) || 'P'}</div>`}
          </div>
          <img src="${stampUrl}" class="stamp" alt="Stamp" />
          <img src="${signUrl}"  class="sign"  alt="Sign"  />
        </div>
      </div>

      <div style="flex:1;"></div> 
      <div class="info">
        <div class="name">${card?.full_name || ''}</div>
        <div class="desig">${card?.designation?.designation_name || card?.designation_name || '-'}</div>
        <div class="loc">${card?.city ? `${card.city}, ${card.state}` : ''}</div>
        
        <div class="idno">${card?.id_number || ''}</div>
      </div>
       <div style="flex:1;"></div> 
      <div class="bottom-bar">
        <!-- Expiry Date Bar: Logic formats date as DD-MMM-YYYY -->
        <div class="bottom-bar-inner">${card?.expiry_date ? new Date(card.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : '-'}</div>
      </div>
    </div>
  </div>

  <!-- BACK DESIGN: Renders address and organizational details -->
  <div class="card-back">
    <div class="back-header">PRESS</div> <!-- Large Back Header -->
    <div class="back-body">
      <div class="addr-title">Card Holder Address</div>
      <div class="addr-text">${card?.address || '-'}</div> <!-- Dynamic: Home Address -->
      <div class="mob-text">Mob : ${card?.mobile || '-'}</div> <!-- Dynamic: Personal Mobile -->
      
      <div class="notice-red">${(notice || '').split(',').map((p, i) => i === 0 ? p + ',' : '<br/>' + p.trim()).join('')}</div> <!-- Notice / Compliance Text -->
      <div class="notice-blue">${(warning || '').split('illegal act,').map((p, i) => i === 0 ? p + 'illegal act,' : '<br/>' + p.trim()).join('')}</div> <!-- Warning / Property Text -->
      
      <div class="logos-row">
        <img src="${badgeUrl}" class="b-badge" /> <!-- Back Side Badge -->
        <img src="${logoUrl}"  class="b-logo" />  <!-- Back Side Logo -->
      </div>
      
      <div class="back-footer">
        <!-- Structural loop mapping: splits long addresses into multi-line blocks for readability -->
        <p class="spaced-regd"><span class="lbl-dark">Regd. Off:</span> <span class="val">${(regdOff || '').split('Floor,').map((part, i, arr) => part.replace(/^\s+/, '') + (i !== arr.length - 1 ? 'Floor,<br/>' : '')).join('')}</span></p>
        <p class="spaced-head"><span class="lbl-dark">Head Off:</span> <span class="val">${(headOff || '').split('West,').map((part, i, arr) => part.replace(/^\s+/, '') + (i !== arr.length - 1 ? 'West,<br/>' : '')).join('')}</span></p>
        <p class="mt-spaced"><span class="lbl">Ph.:</span> <span class="val">${phone}</span></p>
        <p><span class="lbl">Mob:</span> <span class="val">${mob1}, ${mob2}</span></p>
        <p><span class="lbl">E-mail:</span> <span class="val">${email}</span></p>
        <p><span class="lbl">Web:</span> <span class="val">${website}</span></p>
      </div>
    </div>
  </div>

  <!-- Script: Waits for all assets (images/fonts) to load before triggering the print dialog -->
  <script>window.onload=()=>{setTimeout(()=>window.print(),500);}</script>
</body>
</html>`;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  if (cardLoading || settingsLoading) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-2xl border border-white/20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#D0021B] border-t-transparent"></div>
        </div>
      </motion.div>
    );
  }

  const s = settingsData?.settings || {};
  const assets = settingsData?.assets || {};

  const logoUrl = assets.logo?.url;
  const badgeUrl = assets.badge?.url;
  const preambleUrl = assets.preamble?.url;
  const stampUrl = assets.stamp?.url;
  const signUrl = assets.signature?.url;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4" onClick={onClose}>

      {/* Main modal surface pane */}
      <motion.div
        initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25 }}
        className="bg-white dark:bg-[#0F172A] rounded-2xl sm:rounded-[2.5rem] max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-2xl border border-white/20 dark:border-white/10"
        onClick={(e) => e.stopPropagation()} data-testid="card-preview-modal">

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-[#E5E5E5] dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <h2 className="font-serif text-lg sm:text-xl font-bold text-[#1A1A1A] dark:text-white">Press ID Card Preview</h2>
          <button onClick={onClose} className="p-2 text-[#666666] dark:text-gray-400 hover:text-[#D0021B] hover:bg-white dark:hover:bg-gray-700 rounded-xl transition-all shadow-sm">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Card Preview Area */}
        <div className="p-4 overflow-y-auto" style={{ perspective: '1000px' }}>
          <div className="flex justify-center">
            <motion.div className="relative h-[420px]"
              style={{ transformStyle: 'preserve-3d', width: '280px' }}
              animate={{ rotateY: isFlipped ? 180 : 0 }}
              transition={{ duration: 0.6, type: 'spring' }}>

              {/* ── FRONT ── */}
              <div className="absolute inset-0 bg-white rounded shadow-xl overflow-hidden flex"
                style={{ backfaceVisibility: 'hidden', width: '280px', height: '410px', margin: '0 auto', border: '1px solid #E5E5E5' }}>

                {/* Left Red Bar - full height */}
                <div className="bg-[#E31E24] flex flex-col items-center justify-start flex-shrink-0" style={{ width: '67px', paddingTop: '10px', paddingBottom: '2x', gap: '5px' }}>
                  {['P', 'R', 'E', 'S', 'S'].map((ch, i) => (
                    <span key={i} className="text-white font-black select-none" style={{ fontFamily: '"Arial Black", Gadget, sans-serif', fontSize: '80px', lineHeight: '0.9', letterSpacing: '4px', margin: '0px' }}>{ch}</span>
                  ))}
                </div>

                {/* Right Content */}
                <div className="flex-1 flex flex-col relative pl-0 pr-[4px] pt-2 pb-[50px] overflow-hidden">
                  {/* Top Logos */}
                  <div className="flex flex-col items-center">
                    <img src={preambleUrl} alt="Preamble" className="h-[55px] w-auto object-contain mb-[2px]" />
                    <div className="flex items-center justify-center gap-0">
                      {/* --- LOGO ROW DIMENSIONS --- */}
                      <img src={badgeUrl} alt="Badge" className="h-[58px] w-[70px] object-fill " />
                      <img src={logoUrl} alt="Logo" className="h-[58px] w-[98px] object-fill ml-[-1px] " />
                    </div>
                  </div>

                  {/* Govt Info */}
                  <div className="text-center mt-[6px]">
                    <p className="text-[5.5px] font-medium text-[#1A1A1A] uppercase tracking-wide leading-none -mb-[1px]">
                      APPROVED BY
                    </p>
                    <p className="text-[#E31E24] text-[13px] font-bold uppercase">{s.approved_by}</p>
                    <p className="text-[#1A1A1A] text-[7.9px] leading-[23px] font-bold mt-[-5px] w-full text-center" style={{ transform: 'scaleY(2)', transformOrigin: 'center' }}>
                      {s.ministry}
                    </p>
                    <p className="text-[#1A1A1A] text-[11.5px] font-bold mt-[-1px]">RNI: {s.rni_number}</p>
                  </div>

                  {/* QR + Photo - Shifted left using justify-start */}
                  <div className="flex justify-start items-start mt-[2px] pl-0 pr-0 gap-[2px]">
                    {/* QR SECTION */}
                    <div className="w-[105px] h-[108px] bg-white flex items-center justify-center mt-0">
                      {card?.qr_base64 ? (
                        <img src={card.qr_base64} alt="QR Code" className="w-full h-full object-contain" style={{ mixBlendMode: 'multiply' }} />
                      ) : <QrCode className="w-10 h-10 text-[#E5E5E5]" />}
                    </div>

                    {/* PHOTO SECTION - Overlap increased to ml-[-12px] to avoid stamp getting cut off at the edge */}
                    <div className="w-[96px] h-[110px] bg-white p-[1px] relative shrink-0 mt-[0px] ml-[-3px]">
                      <div className="w-full h-full bg-[#f0f0f0] overflow-hidden">
                        {card?.photo_path ? (
                          <img src={`${API_URL}/src/photos/${card.photo_path}`} alt={card?.full_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#666666] text-xl font-medium">
                            {card?.full_name?.charAt(0)?.toUpperCase() || 'P'}
                          </div>
                        )}
                      </div>
                      <img src={stampUrl} alt="Stamp" className="absolute -bottom-[12px] -right-3 w-[60px] h-[60px] object-contain z-10" style={{ mixBlendMode: 'multiply' }} />
                      <img src={signUrl} alt="Signature" className="absolute -bottom-[13px] right-[18px] w-[45px] h-[55px] object-contain z-20" style={{ mixBlendMode: 'multiply' }} />
                    </div>
                  </div>

                  {/* Name / Designation / ID */}
                  <div className="text-center mt-1 mb-2 pb-0 w-full">
                    <p className="font-serif text-[15px] font-black text-black leading-tight">{card?.full_name}</p>
                    <p className="font-times-new-roman text-black text-[12px] font-bold leading-tight mt-[1px]">{card?.designation?.designation_name || card?.designation_name || '-'}</p>
                    <p className="font-times-new-roman text-black text-[11px] font-bold leading-tight mt-[1px]">
                      {card?.city ? `${card.city}, ${card.state}` : ''}
                    </p>
                    
                    <p className="font-times-new-roman text-[12px] text-black font-bold leading-tight mt-[2px]">{card?.id_number}</p>
                  </div>

                  {/* Bottom Bar */}
                  <div className="flex w-full absolute bottom-0 left-0 right-0 justify-center">
                    <div className="flex-1 bg-[#E31E24] text-white text-center py-[4px] font-bold text-[18px] font-sans">
                      {card?.expiry_date ? new Date(card.expiry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* BACK */}
              <div className="absolute inset-0 bg-white rounded shadow-2xl overflow-hidden flex flex-col"
                style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', width: '280px', height: '420px', margin: '0 auto', border: '1px solid #E5E5E5' }}>
                <div className="bg-[#E31E24] text-center shadow-md w-full h-[70px] flex items-center justify-center p-0">
                  <span className="text-white font-black text-[70px] tracking-[0.08em] uppercase w-full" style={{ fontFamily: '"Arial Black", Gadget, sans-serif', letterSpacing: '2px' }}>PRESS</span>
                </div>

                <div className="flex-1 p-2 text-center flex flex-col items-center overflow-hidden">
                  <p className="text-[12px] font-bold text-[#003399]">Card Holder Address</p>
                  <p className="text-[11px] text-[#1A1A1A] font-bold max-w-[90%] leading-tight">{card?.address || '-'}</p>
                  <p className="text-[12px] text-[#1A1A1A] font-bold mb-1">Mob : {card?.mobile || '-'}</p>

                  <p className="text-[11px] text-[#E31E24] leading-tight text-center">
                    {(s.card_back_notice || '').split(',').map((p, i, arr) => (
                      <React.Fragment key={i}>
                        {p.trim()}
                        {i === 0 && arr.length > 1 && <>,<br /></>}
                      </React.Fragment>
                    ))}
                  </p>
                  <p className="text-[11px] text-[#003399] leading-tight mb-1.5 max-w-[100%] text-center">
                    {(s.card_back_warning || '').split('illegal act,').map((p, i, arr) => (
                      <React.Fragment key={i}>
                        {p.trim()}
                        {i === 0 && arr.length > 1 && <>illegal act,<br /></>}
                      </React.Fragment>
                    ))}
                  </p>

                  {/* Dynamic Logos - Back Side */}
                  <div className="flex items-center justify-center gap-0 mb-1">
                    <img src={badgeUrl} alt="Badge" className="h-[65px] w-[80px] object-fill" />
                    <img src={logoUrl} alt="Logo" className="h-[65px] w-[120px] object-fill ml-[-1px]" />
                  </div>

                  {/* Dynamic Footer - Custom built structural loop replacing the target 'Nizamuddin' text 
                    with explicit HTML <br> tags. This acts as a bullet-proof line breaker where standard 
                    CSS flex/wrap rules frequently fail unpredictably across different environments. */}
                  <div className="text-[10px] text-[#555] leading-[1.4] w-full pt-[3px]">
                    <p
                      className="font-bold"
                      style={{ wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}
                    >
                      <span className="text-black font-bold">Regd. Off:</span>{' '}
                      <span className="text-[#111111]">
                        {(s.regd_office || '')
                          .split('Floor,')
                          .map((part, i, arr) => (
                            <React.Fragment key={i}>
                              {part.replace(/^\s+/, '')}
                              {i !== arr.length - 1 && (
                                <>
                                  Floor,
                                  <br />
                                </>
                              )}
                            </React.Fragment>
                          ))}
                      </span>
                    </p>
                    <p
                      className="font-bold mb-1"
                      style={{ wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}
                    >
                      <span className="text-black font-bold leading-tight">Head Off:</span>{' '}
                      <span className="text-[#0D0D0D]">
                        {(s.head_office || '').split('West,').map((part, i, arr) => (
                          <React.Fragment key={i}>
                            {part.replace(/^\s+/, '')}
                            {i !== arr.length - 1 && (
                              <>
                                West,
                                <br />
                              </>
                            )}
                          </React.Fragment>
                        ))}
                      </span>
                    </p>

                    <p>
                      <span className="text-[#E31E24] text-[11px] font-bold leading-tight">Ph.:</span>{' '}
                      <span className="text-[#0D0D0D] text-[11px]">{s.phone_primary}</span>
                    </p>

                    <p>
                      <span className="text-[#E31E24] text-[11px] font-bold leading-tight">Mob:</span>{' '}
                      <span className="text-[#0D0D0D] text-[11px]">
                        {s.mobile_primary}, {s.mobile_secondary}
                      </span>
                    </p>

                    <p>
                      <span className="text-[#E31E24] text-[11px] font-bold leading-tight">E-mail:</span>{' '}
                      <span className="text-[#0D0D0D] text-[11px]">{s.email}</span>
                    </p>

                    <p>
                      <span className="text-[#E31E24] text-[11px] font-bold leading-tight">Web:</span>{' '}
                      <span className="text-[#0D0D0D] text-[11px]">{s.website}</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Flip */}
          <div className="flex justify-center mt-6">
            <button onClick={() => setIsFlipped(!isFlipped)} className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-gray-800 text-[#666666] dark:text-gray-300 font-bold rounded-xl border border-gray-100 dark:border-gray-700 hover:border-[#D0021B] hover:text-[#D0021B] transition-all shadow-sm active:scale-95">
              <RotateCcw className="w-4 h-4" /> Flip Card
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t border-[#E5E5E5] dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 gap-2 sm:gap-0">
          <button onClick={copyVerifyUrl} className="flex items-center justify-center gap-2 px-4 py-2 text-[#666666] dark:text-gray-400 font-bold hover:text-[#D0021B] dark:hover:text-white transition-all rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700">
            <Copy className="w-4 h-4" /> Copy Verify URL
          </button>
          <button onClick={handlePrint} className="flex items-center justify-center gap-2 px-6 py-2.5 bg-[#D0021B] text-white rounded-xl font-bold hover:bg-[#A50015] transition-all shadow-lg shadow-[#D0021B]/20 active:scale-95">
            <Printer className="w-5 h-5" /> Print / Save as PDF
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}